import { Button, Checkbox, Col, Form, Input, message, Row, Select, Tooltip, Upload } from 'antd';
import { ICapturePaymentFormInputFields } from '../OrderInfoSlider/utils';
import { CheckCircleFilled, UploadOutlined } from '@ant-design/icons';
import { useState } from 'react';
import styles from './index.module.scss';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { apiD2r } from 'shared/api';
import errorHandler from 'shared/utils/errorHandler';
import { Loading } from '../Loading';
import { antDesignValidator } from 'helper';
import { EUploadFileKey } from 'shared/api/misc';
import { getPresignedUrl } from 'utils/s3/getPresignedUrl';

interface IPaymentForm {
	invoiceData: any;
	orderId: string;
	dummyReq: (options: any) => void;
	getPaymentLogs: () => void;
	onPaymentCaptured?: () => void;
}

type IChangedValue = {
	[key: string]: string;
};

interface IformData {
	amount: string;
	mode: D2RPaymentType;
	unpaidAmountReason?: UnpaidAmountReason;
	stockReturnAmount?: number;
	otherRemark?: string;
}

export enum D2RPaymentType {
	UPI = 'UPI',
	CASH = 'CASH',
	CHEQUE = 'CHEQUE',
	BANK_TRANSFER = 'BANK_TRANSFER',
	CN_ADJUSTED = 'CN-ADJUSTED',
	N_A = 'N/A'
}

enum UnpaidAmountReason {
	Next_Time = 'NextTime',
	Keyperson_not_there = 'Keyperson not there',
	Stock_not_cleared = 'Stock not cleared',
	Shop_Closed = 'Shop closed',
	Not_visited = 'Not Visited',
	CN_Issue = 'CN Issue',
	STOCK_RETURN = 'Stock Return',
	Bill_2_Bill = 'Bill to Bill',
	Other = 'Other'
}

export const PaymentForm = ({ invoiceData, orderId, dummyReq, getPaymentLogs, onPaymentCaptured }: IPaymentForm) => {
	const [form] = Form.useForm();
	const [isAmountZero, setIsAmountZero] = useState(true);
	const [url, setUrl] = useState();
	const [itemUploadStatus, setItemUploadStatus] = useState<boolean>(false);
	const [isInvoiceClosed, setIsInvoiceClosed] = useState<boolean>(false);
	const [loading, setLoading] = useState(false);
	const [selectedReason, setSelectedReason] = useState<string>('');
	const [enteredAmount, setEnteredAmount] = useState<number>(0);

	const pendingAmount = invoiceData?.billAmount - invoiceData.amountPaid || 0;

	const normalizeAmount = (value: string): number => {
		if (!value || value === '') return 0;
		return Number(value);
	};

	/**
	 * Determines if the reason dropdown should be displayed based on the entered amount and pending amount.
	 * This is useful for the collection head as a reminder, what reason salesman states for partial
	 * payment
	 */
	const shouldShowReasonDropdown = (): boolean => {
		if (enteredAmount === 0 && pendingAmount > 0) return true;
		if (enteredAmount > 0 && enteredAmount < pendingAmount) return true;
		return false;
	};

	const onFileChange = async ({ fileList }: any) => {
		try {
			const file = fileList[0]?.originFileObj;
			//becauase for one upload onFileChange is called many times, so to avoid it
			if (fileList[0].status !== 'done' || !file) return;
			const podType = EUploadFileKey.D2R_PAYMENT_COLLECTION_PROOF;

			try {
				const url = await getPresignedUrl(file, podType, file.type, file.name, {
					orderId
				});
				setUrl(url);
			} catch (err) {
				errorHandler(err);
			}
		} catch (error) {
			errorHandler(error);
		}
	};

	const handleValuesChange = (changedValues: IChangedValue) => {
		if (changedValues.amount) {
			const rawAmount = changedValues.amount;
			const normalizedAmount = normalizeAmount(rawAmount);
			setIsAmountZero(normalizedAmount === 0);
			setEnteredAmount(normalizedAmount);
		}
	};

	const handleReasonChange = (value: string) => {
		setSelectedReason(value);
	};

	const getUnpaidRemarks = (formData: any): string => {
		if (formData.unpaidAmountReason === UnpaidAmountReason.Other) {
			return formData.otherRemark || '';
		}
		return formData.unpaidAmountReason || '';
	};

	async function capturePayment(formData: IformData) {
		try {
			setLoading(true);
			const response = await apiD2r.post('/capture-payment', {
				id: orderId,
				proof: url,
				amount: Number(formData.amount),
				mode: formData.mode,
				unpaidRemarks: getUnpaidRemarks(formData),
				stockReturnAmount: Number(formData.stockReturnAmount ?? 0),
				isInvoiceClosed
			});

			if (response.data.status) {
				message.success('Payment captured successfully');
				onPaymentCaptured?.();
				getPaymentLogs();
			}
		} catch (error) {
			errorHandler(error);
		} finally {
			setLoading(false);
		}
	}

	const onCheckBoxChange = (e: CheckboxChangeEvent) => {
		setIsInvoiceClosed(e.target.checked);
	};

	if (loading) {
		return <Loading />;
	}

	return (
		<Form
			form={form}
			layout="horizontal"
			style={{ marginTop: '2rem' }}
			onFinish={capturePayment}
			onValuesChange={handleValuesChange}
			className={styles.formContainer}
		>
			{ICapturePaymentFormInputFields.map((e) => (
				<Form.Item rules={e.rules} key={e.key} name={e.key} label="Enter Amount" labelCol={{ span: 24 }} wrapperCol={{ span: 24 }}>
					<Input type={e.type} placeholder={e.placeholder} autoFocus style={{ padding: '8px' }} />
				</Form.Item>
			))}
			<Row gutter={16}>
				<Col span={16}>
					<Form.Item
						name="mode"
						rules={[
							() => ({
								required: !isAmountZero,
								message: 'Please select payment mode'
							})
						]}
					>
						<Select placeholder="Select Payment Mode" disabled={isAmountZero} style={{ width: '352px' }}>
							{Object.values(D2RPaymentType).map((mode) => (
								<Select.Option key={mode} value={mode}>
									{mode}
								</Select.Option>
							))}
						</Select>
					</Form.Item>
				</Col>
			</Row>

			<Form.Item style={{ marginBottom: '10px' }}>
				<Row gutter={16} style={{ display: 'flex', flexDirection: 'row' }}>
					<Col span={12}>
						<span>Upload Proof</span>
						<Tooltip title="Image size should be less than 600 KB" placement="right">
							<Upload
								customRequest={dummyReq}
								accept="image/*"
								multiple={false}
								onChange={({ fileList }) => {
									onFileChange({ fileList });
									setItemUploadStatus(true);
								}}
								style={{ marginBlock: '15px' }}
								showUploadList={false}
							>
								<Button
									type="text"
									size="middle"
									className={styles.proof}
									disabled={isAmountZero}
									icon={itemUploadStatus ? <CheckCircleFilled style={{ color: '#228B22' }} /> : <UploadOutlined />}
								></Button>
							</Upload>
						</Tooltip>{' '}
					</Col>

					<Col span={12} style={{ width: '170px' }}>
						<Form.Item valuePropName="checked">
							<Checkbox onChange={onCheckBoxChange} disabled={isAmountZero} className={styles.markClose}>
								Mark as Closed
							</Checkbox>
						</Form.Item>
					</Col>

					{isInvoiceClosed && (
						<Col span={24} style={{ padding: 10 }}>
							<Form.Item
								name="stockReturnAmount"
								label="Enter Stock Return Amount"
								rules={[
									{
										required: false,
										message: ''
									},
									antDesignValidator.positiveNumber
								]}
								labelCol={{ span: 24 }}
								wrapperCol={{ span: 24 }}
							>
								<Input type={'number'} placeholder={'Enter Return Amount'} autoFocus style={{ padding: '8px' }} />
							</Form.Item>
						</Col>
					)}
				</Row>
			</Form.Item>

			{/* Dropdown for Partial payment reason */}
			{shouldShowReasonDropdown() && (
				<Row gutter={18}>
					<Col span={24}>
						<Form.Item
							name="unpaidAmountReason"
							label="Reason for Unpaid/Partial Amount"
							rules={[
								{
									required: true,
									message: 'Please select a reason'
								}
							]}
							labelCol={{ span: 24 }}
							wrapperCol={{ span: 24 }}
						>
							<Select placeholder="Select Reason" onChange={handleReasonChange}>
								{Object.values(UnpaidAmountReason).map((reason) => (
									<Select.Option key={reason} value={reason}>
										{reason}
									</Select.Option>
								))}
							</Select>
						</Form.Item>
					</Col>

					{selectedReason === UnpaidAmountReason.Other && (
						<Col span={24}>
							<Form.Item
								name="otherRemark"
								label="Please specify the reason"
								rules={[
									{
										required: true,
										message: 'Please enter a remark'
									}
								]}
								labelCol={{ span: 24 }}
								wrapperCol={{ span: 24 }}
							>
								<Input placeholder="Enter your remark here" style={{ padding: '8px' }} />
							</Form.Item>
						</Col>
					)}
				</Row>
			)}
			<Form.Item>
				<Button type="primary" htmlType="submit" style={{ width: '100%' }} className={styles.submitBtn}>
					Submit
				</Button>
			</Form.Item>
		</Form>
	);
};
